/**
 * PreferenceManager - Manage class scheduling preferences
 *
 * Dialog component for CRUD operations on class preferences
 * Supports 4 types: room, day, time, teacher with weighted priorities
 */

import React, { useState, useMemo, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Alert,
  Divider,
  CircularProgress,
  Box,
  Slider,
  Chip,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import {
  useGetClassPreferencesQuery,
  useCreateClassPreferenceMutation,
  useUpdateClassPreferenceMutation,
  useDeleteClassPreferenceMutation,
  useGetRoomsQuery,
  useGetTeachersQuery,
} from '../../../api/schedulingApi';
import type { ClassPreference } from '../../../types/scheduling';

interface PreferenceManagerProps {
  open: boolean;
  onClose: () => void;
  classId: string;
  className: string;
  scheduleId: string;
  studioId: string;
}

const DAYS_OF_WEEK = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
] as const;

const PREFERENCE_TYPES = [
  { value: 'room', label: 'Preferred Room' },
  { value: 'day', label: 'Preferred Day' },
  { value: 'time', label: 'Preferred Time' },
  { value: 'teacher', label: 'Preferred Teacher' },
] as const;

const PreferenceManager: React.FC<PreferenceManagerProps> = ({
  open,
  onClose,
  classId,
  className,
  scheduleId,
  studioId,
}) => {
  // Data queries
  const {
    data: preferences,
    isLoading,
    error,
  } = useGetClassPreferencesQuery({ classId, studioId });

  const { data: rooms } = useGetRoomsQuery({ scheduleId, studioId });
  const { data: teachers } = useGetTeachersQuery({ scheduleId, studioId });

  // Mutations
  const [createPreference, { isLoading: isCreating }] =
    useCreateClassPreferenceMutation();
  const [updatePreference, { isLoading: isUpdating }] =
    useUpdateClassPreferenceMutation();
  const [deletePreference, { isLoading: isDeleting }] =
    useDeleteClassPreferenceMutation();

  // Form state
  const [isAddFormOpen, setIsAddFormOpen] = useState(false);
  const [editingPreference, setEditingPreference] =
    useState<ClassPreference | null>(null);
  const [preferenceType, setPreferenceType] = useState<
    'room' | 'day' | 'time' | 'teacher'
  >('day');
  const [preferenceValue, setPreferenceValue] = useState<string>('');
  const [startTime, setStartTime] = useState<string>('09:00');
  const [endTime, setEndTime] = useState<string>('17:00');
  const [weight, setWeight] = useState<number>(5);
  const [formError, setFormError] = useState<string>('');

  // Ref for auto-scroll to form
  const formRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to form when it opens
  useEffect(() => {
    if (isAddFormOpen && formRef.current) {
      // Small delay to ensure the form is rendered
      setTimeout(() => {
        formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }, [isAddFormOpen]);

  // Group preferences by type
  const groupedPreferences = useMemo(() => {
    if (!preferences) return {};

    const grouped: Record<string, ClassPreference[]> = {};
    preferences.forEach((pref: ClassPreference) => {
      if (!grouped[pref.preference_type]) {
        grouped[pref.preference_type] = [];
      }
      grouped[pref.preference_type].push(pref);
    });

    return grouped;
  }, [preferences]);

  const resetForm = () => {
    setPreferenceType('day');
    setPreferenceValue('');
    setStartTime('09:00');
    setEndTime('17:00');
    setWeight(5);
    setFormError('');
    setEditingPreference(null);
  };

  const validateForm = (): boolean => {
    if (preferenceType === 'time') {
      if (startTime >= endTime) {
        setFormError('End time must be after start time');
        return false;
      }
    } else if (!preferenceValue) {
      setFormError('Please select a value');
      return false;
    }

    setFormError('');
    return true;
  };

  const handleOpenAddForm = () => {
    resetForm();
    setIsAddFormOpen(true);
  };

  const handleCloseAddForm = () => {
    setIsAddFormOpen(false);
    resetForm();
  };

  const handleOpenEdit = (pref: ClassPreference) => {
    setEditingPreference(pref);
    setPreferenceType(pref.preference_type);
    setWeight(pref.weight);

    if (pref.preference_type === 'time') {
      const [start, end] = pref.preference_value.split('-');
      setStartTime(start || '09:00');
      setEndTime(end || '17:00');
    } else {
      setPreferenceValue(pref.preference_value);
    }

    setFormError('');
    setIsAddFormOpen(true);
  };

  const handleAddPreference = async () => {
    if (!validateForm()) return;

    try {
      const value =
        preferenceType === 'time' ? `${startTime}-${endTime}` : preferenceValue;

      await createPreference({
        studioId,
        data: {
          class_obj: classId,
          preference_type: preferenceType,
          preference_value: value,
          weight,
        },
      }).unwrap();

      handleCloseAddForm();
    } catch (err) {
      console.error('Failed to create preference:', err);
      setFormError('Failed to create preference');
    }
  };

  const handleUpdatePreference = async () => {
    if (!editingPreference || !validateForm()) return;

    try {
      const value =
        preferenceType === 'time' ? `${startTime}-${endTime}` : preferenceValue;

      await updatePreference({
        id: editingPreference.id,
        studioId,
        data: {
          class_obj: classId,
          preference_type: preferenceType,
          preference_value: value,
          weight,
        },
      }).unwrap();

      handleCloseAddForm();
    } catch (err) {
      console.error('Failed to update preference:', err);
      setFormError('Failed to update preference');
    }
  };

  const handleDelete = async (pref: ClassPreference) => {
    try {
      await deletePreference({
        id: pref.id,
        studioId,
        classId,
      }).unwrap();
    } catch (err) {
      console.error('Failed to delete preference:', err);
    }
  };

  // Format time for display (convert 24h to 12h)
  const formatTime = (time: string): string => {
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    let displayHour = hour;
    if (hour === 0) {
      displayHour = 12;
    } else if (hour > 12) {
      displayHour = hour - 12;
    }
    return `${displayHour}:${minutes} ${ampm}`;
  };

  // Format preference value for display
  const formatPreferenceValue = (pref: ClassPreference): string => {
    switch (pref.preference_type) {
      case 'day':
        return pref.preference_value;
      case 'time': {
        const [start, end] = pref.preference_value.split('-');
        return `${formatTime(start)} - ${formatTime(end)}`;
      }
      case 'room': {
        const room = rooms?.find(r => r.id === pref.preference_value);
        return room ? room.room_name : pref.preference_value;
      }
      case 'teacher': {
        const teacher = teachers?.find(t => t.id === pref.preference_value);
        return teacher ? teacher.teacher_name : pref.preference_value;
      }
      default:
        return pref.preference_value;
    }
  };

  // Get chip color based on weight
  const getChipColor = (
    priorityWeight: number
  ): 'default' | 'warning' | 'error' => {
    if (priorityWeight >= 8) return 'error';
    if (priorityWeight >= 5) return 'warning';
    return 'default';
  };

  // Render dynamic value input based on preference type
  const renderValueInput = () => {
    switch (preferenceType) {
      case 'day':
        return (
          <FormControl fullWidth>
            <InputLabel>Day</InputLabel>
            <Select
              value={preferenceValue}
              onChange={e => setPreferenceValue(e.target.value)}
              label="Day"
            >
              {DAYS_OF_WEEK.map(day => (
                <MenuItem key={day} value={day}>
                  {day}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );

      case 'time':
        return (
          <Stack direction="row" spacing={2}>
            <TextField
              fullWidth
              type="time"
              label="Start Time"
              value={startTime}
              onChange={e => setStartTime(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              fullWidth
              type="time"
              label="End Time"
              value={endTime}
              onChange={e => setEndTime(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Stack>
        );

      case 'room':
        return (
          <FormControl fullWidth>
            <InputLabel>Room</InputLabel>
            <Select
              value={preferenceValue}
              onChange={e => setPreferenceValue(e.target.value)}
              label="Room"
            >
              {rooms?.map(room => (
                <MenuItem key={room.id} value={room.id}>
                  {room.room_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );

      case 'teacher':
        return (
          <FormControl fullWidth>
            <InputLabel>Teacher</InputLabel>
            <Select
              value={preferenceValue}
              onChange={e => setPreferenceValue(e.target.value)}
              label="Teacher"
            >
              {teachers?.map(teacher => (
                <MenuItem key={teacher.id} value={teacher.id}>
                  {teacher.teacher_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{className} - Preferences</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          {/* Current Preferences Display */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Current Preferences
            </Typography>

            {isLoading && (
              <Box display="flex" justifyContent="center" p={2}>
                <CircularProgress size={24} />
              </Box>
            )}
            {!isLoading && error && (
              <Alert severity="error">Failed to load preferences</Alert>
            )}
            {!isLoading &&
              !error &&
              (!preferences || preferences.length === 0) && (
                <Alert severity="info">
                  No preferences set. Add preferences below to guide the
                  scheduling algorithm.
                </Alert>
              )}
            {!isLoading && !error && preferences && preferences.length > 0 && (
              <List>
                {PREFERENCE_TYPES.map(({ value, label }) => {
                  const prefs = groupedPreferences[value];
                  if (!prefs || prefs.length === 0) return null;

                  return (
                    <React.Fragment key={value}>
                      <ListItem>
                        <ListItemText
                          primary={
                            <Typography variant="subtitle2" fontWeight="bold">
                              {label}
                            </Typography>
                          }
                        />
                      </ListItem>
                      {prefs.map(pref => (
                        <ListItem
                          key={pref.id}
                          secondaryAction={
                            <Stack direction="row" spacing={0.5}>
                              <IconButton
                                size="small"
                                onClick={() => handleOpenEdit(pref)}
                                disabled={isUpdating || isDeleting}
                                title="Edit"
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDelete(pref)}
                                disabled={isDeleting}
                                title="Delete"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Stack>
                          }
                          sx={{ pl: 4 }}
                        >
                          <ListItemText
                            primary={formatPreferenceValue(pref)}
                            secondary={
                              <Chip
                                label={`Priority: ${pref.weight}`}
                                size="small"
                                color={getChipColor(pref.weight)}
                                sx={{ mt: 0.5 }}
                              />
                            }
                          />
                        </ListItem>
                      ))}
                    </React.Fragment>
                  );
                })}
              </List>
            )}
          </Box>

          <Divider />

          {/* Add/Edit Form */}
          {isAddFormOpen ? (
            <Box ref={formRef}>
              <Typography variant="subtitle2" gutterBottom>
                {editingPreference ? 'Edit Preference' : 'Add New Preference'}
              </Typography>
              <Stack spacing={2}>
                {formError && <Alert severity="error">{formError}</Alert>}

                <FormControl fullWidth>
                  <InputLabel>Preference Type</InputLabel>
                  <Select
                    value={preferenceType}
                    onChange={e => {
                      setPreferenceType(
                        e.target.value as 'room' | 'day' | 'time' | 'teacher'
                      );
                      setPreferenceValue('');
                    }}
                    label="Preference Type"
                  >
                    {PREFERENCE_TYPES.map(({ value, label }) => (
                      <MenuItem key={value} value={value}>
                        {label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {renderValueInput()}

                <Box>
                  <Typography gutterBottom>
                    Priority Weight: {weight}
                  </Typography>
                  <Slider
                    value={weight}
                    onChange={(e, newValue) => setWeight(newValue as number)}
                    min={1}
                    max={10}
                    marks
                    valueLabelDisplay="auto"
                  />
                  <Typography variant="caption" color="text.secondary">
                    Higher values = higher priority for the scheduling algorithm
                  </Typography>
                </Box>

                <Stack direction="row" spacing={1}>
                  {editingPreference ? (
                    <Button
                      variant="contained"
                      startIcon={<SaveIcon />}
                      onClick={handleUpdatePreference}
                      disabled={isUpdating}
                    >
                      Save Changes
                    </Button>
                  ) : (
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={handleAddPreference}
                      disabled={isCreating}
                    >
                      Add Preference
                    </Button>
                  )}
                  <Button onClick={handleCloseAddForm}>Cancel</Button>
                </Stack>
              </Stack>
            </Box>
          ) : (
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleOpenAddForm}
            >
              Add Preference
            </Button>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default PreferenceManager;
